import React from 'react'
import PropTypes from 'prop-types'
import { Card, Typography, Badge, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faXRay, faCamera, faNotesMedical, faImage, faStethoscope } from '@fortawesome/free-solid-svg-icons'

const { Text } = Typography

const categoryIcons = {
  'x-ray': faXRay,
  photo: faCamera,
  scan: faNotesMedical,
  general: faImage,
  clinical: faStethoscope
}

const ImageThumbnail = ({ image, onClick }) => {
  const getCategoryIcon = (category) => {
    return categoryIcons[category] || faImage
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (date) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <Card
      hoverable
      onClick={onClick}
      className='image-thumbnail-card'
      style={{
        width: 120,
        height: 120,
        cursor: 'pointer',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative'
      }}
      styles={{
        body: {
          padding: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Image Container */}
      <div
        style={{
          height: '80px',
          width: '100%',
          backgroundImage: `url(${image.thumbnailData || image.imageData})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          backgroundColor: '#f5f5f5'
        }}
      >
        {/* Overlay with category icon and view icon */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.3))',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '6px'
          }}
        >
          {/* Category Badge */}
          <Badge
            count={
              <FontAwesomeIcon
                icon={getCategoryIcon(image.imageCategory)}
                style={{
                  color: 'white',
                  fontSize: '10px',
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  padding: '2px 4px',
                  borderRadius: '4px'
                }}
              />
            }
            style={{ backgroundColor: 'transparent' }}
          />

          {/* View Icon */}
          <div
            style={{
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FontAwesomeIcon
              icon={faEye}
              style={{
                color: 'white',
                fontSize: '10px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Image Info */}
      <div
        style={{
          padding: '6px 8px',
          height: '40px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: 'white'
        }}
      >
        <Tooltip title={`${image.imageName}${image.imageNote ? '\n' + image.imageNote : ''}`}>
          <Text
            strong
            style={{
              fontSize: '10px',
              lineHeight: '1.2',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginBottom: '2px',
              textAlign: 'center',
              color: '#595959'
            }}
          >
            {formatDate(image.uploadedAt)}
          </Text>
        </Tooltip>

        <Text
          type='secondary'
          style={{
            fontSize: '9px',
            lineHeight: '1',
            textAlign: 'center',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {image.imageName || 'Untitled'}
        </Text>
      </div>

      {/* Note indicator */}
      {image.imageNote && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            right: '28px',
            backgroundColor: '#52c41a',
            borderRadius: '50%',
            width: '8px',
            height: '8px',
            border: '1px solid white'
          }}
          title='Has notes'
        />
      )}
    </Card>
  )
}

ImageThumbnail.propTypes = {
  image: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    imageName: PropTypes.string,
    imageNote: PropTypes.string,
    imageData: PropTypes.string.isRequired,
    thumbnailData: PropTypes.string,
    imageCategory: PropTypes.string,
    uploadedAt: PropTypes.string,
    fileSize: PropTypes.number
  }).isRequired,
  onClick: PropTypes.func.isRequired
}

export default ImageThumbnail
